import {sha256} from "js-sha256"
import {FieldStatus} from "@/enums/FieldStatus";
import ApiService from "./ApiService";
import Vue from 'vue';
// import settings from '../settings'

const background = (status) =>{
    // console.log(status);
    switch (status) {
        case 'Plan':
            return 'yellow';
        case 'Busy':
            return 'blue';
        case 'Final':
            return 'green lighten-2';
        case '-':
            return 'green lighten-2';
        default:
            return 'white';
    }
}

const groupBy = (xs, key) => {
    return xs.reduce(function(rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  const distinct = (value, index, self) => {
    return self.indexOf(value) === index
  };

  const isRns = (esv) => {
    return esv.serviceRequestTypeId === 24;
};

const getServiceRequestTypeId = (esv) => {
    return esv.serviceRequestTypeId || null;
};

// Update the common utility functions
/**
 * Determines the status/visibility of a specific field in an ESV document.
 *
 * This function first checks the service request type settings. If the service type status
 * is Keep or Disable, these take precedence over any document-specific settings.
 * Only when the service type status is ShowEnabled or ShowDisabled, document-specific
 * settings can override the default behavior.
 *
 * @param {Object} store - Vuex store instance containing service request type settings
 * @param {Object} esv - ESV document object containing document-specific field settings
 * @param {string} fieldName - Dot-notation path of the field
 * @returns {number} Field status code from FieldStatus enum
 */
const getFieldStatus = (store, esv, fieldName) => {
    // First get the service request type setting
    const serviceRequestTypeId = getServiceRequestTypeId(esv);
    const serviceTypeStatus = store.getters.getServiceRequestTypeSettings(serviceRequestTypeId, fieldName) || FieldStatus.Keep;

    // If service type status is Keep or Disable, it takes precedence
    if (serviceTypeStatus === FieldStatus.Keep || serviceTypeStatus === FieldStatus.Disable) {
        return serviceTypeStatus;
    }

    // For ShowEnabled or ShowDisabled, allow document-specific settings to override
    if (esv.documentFieldSettings) {
        const documentSetting = esv.documentFieldSettings.find(dfs => dfs.fieldName === fieldName);
        if (documentSetting) {
            return documentSetting.fieldStatus;
        }
    }

    // Fall back to service type status if no document-specific setting exists
    return serviceTypeStatus;
};
const getEffectiveFieldStatus = (store, esv, fieldName) => {
    if (esv.effectiveFieldSettings && esv.effectiveFieldSettings[fieldName] !== undefined) {
        return esv.effectiveFieldSettings[fieldName];
    }
    return FieldStatus.Keep; // Default fallback
};

const isFieldVisible = (store, esv, fieldName) => {
    const status = getFieldStatus(store, esv, fieldName);
    return FieldStatus.isVisible(status);
};

const isFieldEditable = (store, esv, fieldName) => {
    const status = getFieldStatus(store, esv, fieldName);
    return FieldStatus.isEditable(status);
};

const passwordLength = (value) => {
    return (!!value && value.length >= 8) || "Password must be at least 8 characters"
}

const imageUrlCache = new Map();
const pendingImageFetches = new Map(); // Track in-flight requests to prevent duplicates

// Constant identifier pattern for image URLs
const IMAGE_IDENTIFIER_PATTERN = 'daikin-secure';

// Cache operations with normalized keys
const normalizedImageCache = {
  set(key, value) {
    const normalizedKey = key.split('?')[0];
    imageUrlCache.set(normalizedKey, value);
  },
  get(key) {
    const normalizedKey = key.split('?')[0];
    return imageUrlCache.get(normalizedKey);
  },
  has(key) {
    const normalizedKey = key.split('?')[0];
    return imageUrlCache.has(normalizedKey);
  },
  entries() {
    return imageUrlCache.entries();
  },
  clear() {
    imageUrlCache.clear();
    pendingImageFetches.clear();
  },
  delete(key) {
    const normalizedKey = key.split('?')[0];
    imageUrlCache.delete(normalizedKey);
  },
  values() {
    return imageUrlCache.values();
  }
};

async function convertImagesToBlobs(html) {
    if (!html) return html;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Process both images and our loading containers
    const images = doc.getElementsByTagName('img');
    const loadingContainers = doc.querySelectorAll('.image-loading-container');
    
    // If no elements to process, return original content
    if (images.length === 0 && loadingContainers.length === 0) {
        return html;
    }
    
    // Process images in batches to avoid too many concurrent requests
    const batchSize = 3; // Process 3 images at a time
    const imagesToProcess = [];
    
    // Process regular images first
    for (const img of images) {
        const src = img.getAttribute('src');
        
        // Skip blob URLs and non-backend images
        if (!src || src.startsWith('blob:') || !src.includes(`/api/Images/${IMAGE_IDENTIFIER_PATTERN}/`)) {
            continue;
        }
        
        // Check cache first
        if (normalizedImageCache.has(src)) {
            const cachedUrl = normalizedImageCache.get(src);
            
            // Skip error markers
            if (cachedUrl.startsWith('ERROR:')) {
                // Add error styling
                img.classList.add('image-load-error');
                img.setAttribute('title', 'Failed to load image: ' + src);
                img.setAttribute('data-load-error', 'true');
                continue;
            }
            
            // Use cached blob URL
            img.setAttribute('src', cachedUrl);
            continue;
        }
        
        // Check if this image is already being fetched
        if (pendingImageFetches.has(src)) {
            // Wait for the existing fetch to complete
            try {
                const blobUrl = await pendingImageFetches.get(src);
                img.setAttribute('src', blobUrl);
                continue;
            } catch (error) {
                // The pending fetch failed, but we already handled it
                continue;
            }
        }
        
        // Add to processing queue
        imagesToProcess.push({element: img, src, type: 'img'});
    }
    
    // Process loading containers
    for (const container of loadingContainers) {
        const src = container.getAttribute('data-original-src');
        
        // Skip if no source or already processed
        if (!src || !src.includes(`/api/Images/${IMAGE_IDENTIFIER_PATTERN}/`)) {
            continue;
        }
        
        // Check cache first
        if (normalizedImageCache.has(src)) {
            const cachedUrl = normalizedImageCache.get(src);
            
            // Skip error markers
            if (cachedUrl.startsWith('ERROR:')) {
                // Create error container
                const errorContainer = doc.createElement('div');
                errorContainer.className = 'image-error-container';
                errorContainer.style.border = '1px solid #f44336';
                errorContainer.style.borderRadius = '4px';
                errorContainer.style.padding = '8px';
                errorContainer.style.margin = '8px 0';
                errorContainer.style.backgroundColor = '#ffebee';
                errorContainer.style.color = '#d32f2f';
                errorContainer.style.textAlign = 'center';
                
                errorContainer.innerHTML = `
                    <div style="font-weight: bold; margin-bottom: 4px;">Image Failed to Load</div>
                    <div style="font-size: 12px; word-break: break-all; overflow: hidden; text-overflow: ellipsis;">
                        ${src}
                    </div>
                `;
                
                container.parentNode.replaceChild(errorContainer, container);
                continue;
            }
            
            // Replace container with image using cached blob URL
            const img = doc.createElement('img');
            img.setAttribute('src', cachedUrl);
            container.parentNode.replaceChild(img, container);
            continue;
        }
        
        // Check if this image is already being fetched
        if (pendingImageFetches.has(src)) {
            // Wait for the existing fetch to complete
            try {
                const blobUrl = await pendingImageFetches.get(src);
                const img = doc.createElement('img');
                img.setAttribute('src', blobUrl);
                container.parentNode.replaceChild(img, container);
                continue;
            } catch (error) {
                // The pending fetch failed, but we already handled it
                const img = doc.createElement('img');
                img.setAttribute('src', src);
                container.parentNode.replaceChild(img, container);
                continue;
            }
        }
        
        // Add to processing queue
        imagesToProcess.push({element: container, src, type: 'container'});
    }
    
    // Process elements in batches
    for (let i = 0; i < imagesToProcess.length; i += batchSize) {
        const batch = imagesToProcess.slice(i, i + batchSize);
        
        // Process batch in parallel
        await Promise.all(batch.map(async ({element, src, type}) => {
            // Double-check that this isn't already being processed
            if (pendingImageFetches.has(src)) {
                try {
                    const blobUrl = await pendingImageFetches.get(src);
                    if (type === 'img') {
                        element.setAttribute('src', blobUrl);
                    } else if (type === 'container') {
                        const img = doc.createElement('img');
                        img.setAttribute('src', blobUrl);
                        element.parentNode.replaceChild(img, element);
                    }
                    return;
                } catch (error) {
                    // Continue with normal processing if the pending fetch failed
                }
            }
            
            // Create a promise for this fetch that can be shared
            const fetchPromise = (async () => {
                try {
                    // Fix duplicated URLs by extracting the path with the pattern
                    let apiPath = src;
                    
                    const patternIndex = src.lastIndexOf(`/api/Images/${IMAGE_IDENTIFIER_PATTERN}/`);
                    if (patternIndex !== -1) {
                        apiPath = src.substring(patternIndex);
                    }
                    
                    // The URL should now have the correct format with the pattern in the path
                    const response = await ApiService.fetchImage(apiPath);
                    
                    if (!response.data) {
                        throw new Error('No data received from image fetch');
                    }
                    
                    const blob = response.data; // Response is already a blob due to responseType setting
                    const blobUrl = URL.createObjectURL(blob);
                    
                    // Cache the blob URL
                    normalizedImageCache.set(src, blobUrl);
                    
                    // Remove from pending fetches
                    pendingImageFetches.delete(src);
                    
                    return blobUrl;
                } catch (error) {
                    console.error('Failed to convert image to blob:', error, 'for src:', src);
                    
                    // Mark as error in cache to prevent retries
                    normalizedImageCache.set(src, 'ERROR: ' + error.message);
                    
                    // Remove from pending fetches
                    pendingImageFetches.delete(src);
                    
                    throw error;
                }
            })();
            
            // Store the promise in pending fetches
            pendingImageFetches.set(src, fetchPromise);
            
            try {
                const blobUrl = await fetchPromise;
                
                // Handle based on element type
                if (type === 'img') {
                    // Just update the src
                    element.setAttribute('src', blobUrl);
                } else if (type === 'container') {
                    // Replace container with image
                    const img = doc.createElement('img');
                    img.setAttribute('src', blobUrl);
                    element.parentNode.replaceChild(img, element);
                }
            } catch (error) {
                // Handle error based on element type
                if (type === 'container') {
                    // Replace loading container with original image on error
                    const img = doc.createElement('img');
                    img.setAttribute('src', src);
                    element.parentNode.replaceChild(img, element);
                }
                // For regular images, just leave as is
            }
        }));
    }
    
    const result = doc.body.innerHTML;
    return result;
}

const cleanupImageUrlCache = () => {
    // Revoke all object URLs to prevent memory leaks
    for (const blobUrl of normalizedImageCache.values()) {
        if (blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrl);
        }
    }
    normalizedImageCache.clear();
};

// Function to convert blob URLs back to backend URLs for saving
const convertBlobsToBackendUrls = (html) => {
    if (!html) return html;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = doc.getElementsByTagName('img');
    
    for (const img of images) {
        const src = img.getAttribute('src');
        
        // Skip non-blob URLs
        if (!src || !src.startsWith('blob:')) {
            continue;
        }
        
        // Find the backend URL for this blob URL
        for (const [cacheKey, blobUrl] of normalizedImageCache.entries()) {
            if (blobUrl === src) {
                // Extract the base URL without the source and timestamp parameters
                const baseUrl = cacheKey.split('?')[0];
                
                // Use the backend URL which should be in the correct format
                // If it's a duplicated URL, extract just the path with the pattern
                let normalizedUrl = baseUrl;
                const patternIndex = baseUrl.lastIndexOf(`/api/Images/${IMAGE_IDENTIFIER_PATTERN}/`);
                if (patternIndex !== -1) {
                    normalizedUrl = baseUrl.substring(patternIndex);
                }
                
                img.setAttribute('src', normalizedUrl);
                // Remove the source attribute as it's not needed in the saved content
                img.removeAttribute('data-source');
                break;
            }
        }
    }
    
    return doc.body.innerHTML;
};

// Consolidated image utilities
const ImageUtils = {
    // Constants for image processing
    MAX_SIZE_MB: 5,
    STANDARD_MAX_DIMENSION: 1600,
    LARGE_MAX_DIMENSION: 1200,
    STANDARD_QUALITY: 0.7,
    REDUCED_QUALITY: 0.6,
    SECONDARY_QUALITY: 0.5,
    SECONDARY_SCALE: 0.8,
    
    // Check if image needs resizing based on dimensions
    async needsResizing(file) {       
        const dimensions = await this.getImageDimensions(file);
        return dimensions.width > this.STANDARD_MAX_DIMENSION || 
               dimensions.height > this.STANDARD_MAX_DIMENSION;
    },
    
    // Get image dimensions without modifying the image
    getImageDimensions(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            
            reader.onload = (event) => {
                const img = new Image();
                
                img.onerror = (error) => {
                    reject(error);
                };
                
                img.onload = () => {
                    resolve({
                        width: img.width,
                        height: img.height
                    });
                };
                
                img.src = event.target.result;
            };
            
            reader.onerror = (error) => {
                reject(error);
            };
        });
    },
    
    // Resize and compress image
    async compressImage(file, maxSizeMB = 5, forceResize = false) {
        // Skip compression for SVG files and GIFs
        if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
            return file;
        }
        
        // Calculate size in MB
        const fileSizeMB = file.size / (1024 * 1024);
        
        // Skip if file is already smaller than max size and not forcing resize
        if (fileSizeMB <= maxSizeMB && !forceResize) {
            // Even if under size limit, check dimensions if forcing resize
            if (forceResize) {
                const needsResize = await this.needsResizing(file);
                if (!needsResize) {
                    return file;
                }
            } else {
                return file;
            }
        }
        
        try {
            // Get image as data URL
            const imageData = await this.fileToDataUrl(file);
            
            // Create image
            const dimensions = await this.getImageDimensions(file);
            let { width, height } = dimensions;
            
            // Calculate dimensions and quality
            let quality = this.STANDARD_QUALITY;
            let dimensionsChanged = false;
            
            // For very large images or when forcing resize
            if (width > this.STANDARD_MAX_DIMENSION || 
                height > this.STANDARD_MAX_DIMENSION || 
                forceResize) {
                const ratio = Math.min(
                    this.STANDARD_MAX_DIMENSION / width, 
                    this.STANDARD_MAX_DIMENSION / height
                );
                width = Math.floor(width * ratio);
                height = Math.floor(height * ratio);
                dimensionsChanged = true;
            }
            
            // For extremely large images
            if (fileSizeMB > 10) {
                const ratio = Math.min(
                    this.LARGE_MAX_DIMENSION / width, 
                    this.LARGE_MAX_DIMENSION / height
                );
                width = Math.floor(width * ratio);
                height = Math.floor(height * ratio);
                quality = this.REDUCED_QUALITY;
                dimensionsChanged = true;
            }
            
            if (!dimensionsChanged && forceResize) {
                console.log('WARNING: No dimension changes applied even though processing was requested!');
            }
            
            // Create canvas and draw image
            let blob = await this.createResizedBlob(imageData, width, height, file.type, quality);
            
            // If still too large, apply secondary compression for supported formats
            const blobSizeMB = blob.size / (1024 * 1024);
            if (blobSizeMB > maxSizeMB && 
                (file.type.includes('jpeg') || file.type.includes('jpg') || file.type.includes('png'))) {
                
                width = Math.floor(width * this.SECONDARY_SCALE);
                height = Math.floor(height * this.SECONDARY_SCALE);
                
                blob = await this.createResizedBlob(
                    imageData, 
                    width, 
                    height, 
                    file.type, 
                    this.SECONDARY_QUALITY
                );
            }
            
            // Create file from blob
            return new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
            });
        } catch (error) {
            console.error('Image compression error:', error);
            throw error;
        }
    },
    
    // Convert file to data URL
    fileToDataUrl(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            
            reader.onload = (event) => {
                resolve(event.target.result);
            };
            
            reader.onerror = (error) => {
                reject(error);
            };
        });
    },
    
    // Create a resized blob from image data
    createResizedBlob(imageData, width, height, type, quality) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onerror = (error) => {
                reject(error);
            };
            
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob conversion failed'));
                            return;
                        }
                        resolve(blob);
                    }, type, quality);
                } catch (error) {
                    reject(error);
                }
            };
            
            img.src = imageData;
        });
    },

    async convertWebpToPng(file) {
        if (file.type !== 'image/webp') {
            console.warn("convertWebpToPng called with non-webp file:", file.name);
            return file; // Return original if not WEBP
        }

        try {
            const imageData = await this.fileToDataUrl(file);
            const dimensions = await this.getImageDimensions(file);

            // Convert using canvas, maintaining original dimensions and aiming for lossless conversion
            const blob = await this.createResizedBlob(imageData, dimensions.width, dimensions.height, 'image/png', 1.0); // Use PNG type, full quality

            // Update filename
            let finalFileName = file.name;
            if (file.name.toLowerCase().endsWith('.webp')) {
                const nameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.'));
                finalFileName = `${nameWithoutExtension}.png`;
            } else {
                 // Append .png if original didn't have .webp extension (unlikely but possible)
                 finalFileName = `${file.name}.png`;
            }

            return new File([blob], finalFileName, {
                type: 'image/png',
                lastModified: Date.now()
            });

        } catch (error) {
            console.error('WEBP to PNG conversion error:', error);
            Vue.$toast.error("Failed to convert WEBP image to PNG. Please try a different format.");
            throw error; // Re-throw to be caught by handleImageFile
        }
    }
};

const compressImage = (file, maxSizeMB = 5, forceResize = false) => {
    return ImageUtils.compressImage(file, maxSizeMB, forceResize);
};

export { background, groupBy, distinct, isRns, getServiceRequestTypeId, getFieldStatus, getEffectiveFieldStatus, isFieldVisible, isFieldEditable, passwordLength, convertImagesToBlobs, cleanupImageUrlCache, convertBlobsToBackendUrls, compressImage, ImageUtils };

export default {
    background,
    groupBy,
    distinct,
    isRns,
    getServiceRequestTypeId,
    getFieldStatus,
    getEffectiveFieldStatus,
    isFieldVisible,
    isFieldEditable,
    passwordLength,
    cleanupImageUrlCache,
    convertBlobsToBackendUrls,
    rules: {
        required: value => !!value || "Required field",
        requiredArray: value => (Array.isArray(value) ? value.length > 0 : !!value) || "Required field",
        ip: value => {
            const pattern = /^(localhost|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|(::[0-9]))$/
            var ipRange = value.split('-')
            var returnValue;

            ipRange.forEach(ip => {
                returnValue = pattern.test(ip) || 'Invalid IP or IP range'
            });

            return returnValue
        },
        decimal: value => {
            if (value && value.toString().includes(',')){
                return "Decimals must use . separator";
            }
            const pattern = /^(\d+(?:[.]\d{1,5})?|)$/
            return (pattern.test(value) || !value) || "Value must be a decimal"
        },
        integer: value => {
            const pattern = /^[-+]?\d*$/
            return pattern.test(value) || "Value must be a number"
        },
        password: value => {
            const passwordRules = [passwordLength] //Add rules here, to create new pw requirements
            var valid = 0;
            var message = [];

            passwordRules.forEach(r => {
                const ruleResult = r(value);
                if (ruleResult !== true) {
                    message.push(ruleResult);
                }
                else {
                    valid++;
                }
            })

            return  valid === passwordRules.length || message.join(', ');
        },
        notZero: value => {
            return (!value || (!isNaN(value) && value > 0) || parseFloat(value.replace(",","")) > 0.0000) || 'Value cannot be zero'
        },
        positive: value => {
            return (!value || (!isNaN(value) && value > 0)) || 'Value should be greater than 0'
        },
        email: value => {
            const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return (!!value && pattern.test(value)) || "Email is not valid";
        },
        dossier: value => {
            return (!!value && value.length <= 7) || "Dossier number can be maximum 7 characters"
        }
    },
    readError (error) {
        console.error(error.message)
        console.error(error.response)
        if (!!error.response && !!error.response.data){
            if (error.response.data.detail){
                return error.response.data.detail;
            }
            else if (error.response.data.errors) {
                var errorString = '';
                Object.keys(error.response.data.errors).forEach(e => {
                    console.error(error.response.data.errors[e]);
                    errorString += error.response.data.errors[e].join('</br>');
                })
                console.error(error.response.data.errors);
                return errorString
            }
            return error.response.data
        }
        else if (error.message) {
            return error.message
        }
        else{
            return error
        }
    },
    hash (message) {
        if (message === null){
            return null
        }
        return sha256(message)
    },
    download(resp) {
        var fileName = resp.headers['filename'];
        var objectUrl = window.URL.createObjectURL(new File([resp.data], fileName, {type: resp.headers['content-type']}));  
        // this.$common.download(objectUrl, fileName) 

        var link = document.createElement('a')
        link.href = objectUrl
        link.download = fileName
        link.click()
    },
    ckEditorConfig: {
        extraPlugins: 'colorbutton,colordialog,justify,clipboard',
        colorButton_colors:  '000,FFF,FF0000,00FF00,0000FF',
        colorButton_enableMore: 'true',
        removeButtons: 'Subscript,Superscript',
        imageResize: {
            maxWidth: 500,
            maxHeight: 500
        },
        disableObjectResizing: true,
        versionCheck: false,
        // Maximum file size limit (5MB)
        imageUploadMaxSize: 5 * 1024 * 1024,
        // Allowed image types
        imageAllowedTypes: /image\/(jpeg|jpg|png|gif|bmp|webp|svg\+xml)/,
        // Remove filebrowserUploadUrl as we're handling uploads via our own handlers
        filebrowserUploadUrl: null,
        // Disable default image upload button as we're handling it via paste/drop
        removePlugins: 'uploadimage,uploadfile,imageResize'
    },
    setupCKEditor(ckeditor) {
        // The initial content should already be processed with blob URLs when switching to edit mode
        
        //check multiple times with exponential backoff
        let attempts = 0;
        const maxAttempts = 3;
        const checkAndProcess = () => {
            attempts++;
            const initialData = ckeditor.getData();
            
                         // Only process if we have unprocessed backend URLs
             if (initialData && 
                 initialData.includes(`/api/Images/${IMAGE_IDENTIFIER_PATTERN}/`)) {
                 
                 console.warn('⚠️ CKEditor received unprocessed content, processing now...');
                 
                 // Check if this content is already being processed
                 if (pendingImageFetches.has(initialData)) {
                     return; // Skip if already processing
                 }
                 
                 convertImagesToBlobs(initialData).then(processedContent => {
                    if (processedContent !== initialData) {
                        // Use a short delay for setData since we've already waited
                        setTimeout(() => {
                            ckeditor.setData(processedContent);
                        }, 25); 
                    }
                }).catch(error => {
                    console.error('❌ CKEDITOR: Error processing initial content in setupCKEditor:', error);
                });
            } else if (attempts < maxAttempts && !initialData) {
                // If no data yet and we haven't exceeded attempts, try again
                setTimeout(checkAndProcess, 50 * attempts); // Exponential backoff: 50ms, 100ms, 150ms
            }
        };
        
        // Start with initial delay to let v-model settle
        setTimeout(checkAndProcess, 100);
        
        // Add image size normalization
        const MAX_IMAGE_HEIGHT = 900; // Maximum allowed height in pixels
        
        // Normalize image dimensions function
        function normalizeImageDimensions() {
            const editorDocument = ckeditor.document;
            const images = editorDocument.find('img');
            let dimensionsChanged = false;
            
            if (images.count() > 0) {
                for (let i = 0; i < images.count(); i++) {
                    const img = images.getItem(i);
                    let style = img.getAttribute('style') || '';
                    
                    // Parse existing height and width from style
                    let height = null;
                    let width = null;
                    
                    const heightMatch = style.match(/height\s*:\s*(\d+)px/i);
                    if (heightMatch) {
                        height = parseInt(heightMatch[1], 10);
                    }
                    
                    const widthMatch = style.match(/width\s*:\s*(\d+)px/i);
                    if (widthMatch) {
                        width = parseInt(widthMatch[1], 10);
                    }
                    
                    // Track if dimensions were changed
                    let heightChanged = false;
                    let widthChanged = false;
                    
                    // Apply max height if needed (ONLY check height, not width)
                    if (height && height > MAX_IMAGE_HEIGHT) {
                        // Calculate proportional width if we have both dimensions
                        if (width) {
                            const ratio = MAX_IMAGE_HEIGHT / height;
                            width = Math.floor(width * ratio);
                            widthChanged = true;
                        }
                        height = MAX_IMAGE_HEIGHT;
                        heightChanged = true;
                    }
                    
                    // Update style if dimensions changed
                    if (heightChanged || widthChanged) {
                        // Remove existing height/width
                        style = style.replace(/height\s*:\s*\d+px\s*;?/ig, '');
                        style = style.replace(/width\s*:\s*\d+px\s*;?/ig, '');
                        
                        // Add new dimensions
                        if (height) {
                            style += `height: ${height}px; `;
                        }
                        if (width) {
                            style += `width: ${width}px; `;
                        }
                        
                        img.setAttribute('style', style);
                        
                        // Set flag if dimensions were capped
                        dimensionsChanged = true;
                    }
                }
            }
            
            // Show toast notification if dimensions were capped
            if (dimensionsChanged) {
                Vue.$toast.info(
                    `Image height has been automatically adjusted to maximum allowed size (${MAX_IMAGE_HEIGHT}px).`,
                    {
                        duration: 4000,
                        position: "top-center"
                    }
                );
            }
        }
        
        // Apply normalization when content changes
        ckeditor.on('change', normalizeImageDimensions);
        
        // Apply normalization when editor is ready
        ckeditor.on('instanceReady', normalizeImageDimensions);
        
        // Handle image URL additions by watching for img elements being added to the editor
        ckeditor.on('contentDom', function() {
            // Get the editable element
            const editable = ckeditor.editable();
            const editableElement = editable.$;
            
            if (!editableElement) {
                return;
            }
            
            // Create image load handler
            const handleImageLoad = function(imgNode) {
                // Images inserted via URL might not have style attributes set
                // Set natural dimensions as attributes if not already present
                if (imgNode.naturalWidth && imgNode.naturalHeight) {
                    const style = imgNode.getAttribute('style') || '';
                    
                    // Only add natural dimensions if no dimensions are specified
                    if (!style.match(/width\s*:/i) && !style.match(/height\s*:/i)) {
                        // Check if we need to constrain height and maintain aspect ratio
                        let width = imgNode.naturalWidth;
                        let height = imgNode.naturalHeight;
                        
                        if (height > MAX_IMAGE_HEIGHT) {
                            const ratio = MAX_IMAGE_HEIGHT / height;
                            width = Math.floor(width * ratio);
                            height = MAX_IMAGE_HEIGHT;
                            
                            // Show notification about height adjustment
                            Vue.$toast.info(
                                `Image height has been automatically adjusted to maximum allowed size (${MAX_IMAGE_HEIGHT}px).`,
                                {
                                    duration: 4000,
                                    position: "top-center"
                                }
                            );
                        }
                        
                        const newStyle = style + `width: ${width}px; height: ${height}px;`;
                        imgNode.setAttribute('style', newStyle);
                    } 
                } 
            };
            
            // Create a MutationObserver to watch for new images
            const observer = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    // Check for added nodes
                    if (mutation.addedNodes && mutation.addedNodes.length) {
                        for (let i = 0; i < mutation.addedNodes.length; i++) {
                            const node = mutation.addedNodes[i];
                            
                            // Direct image node
                            if (node.nodeName && node.nodeName.toLowerCase() === 'img') {
                                if (node.complete) {
                                    handleImageLoad(node);
                                } else {
                                    node.addEventListener('load', function() {
                                        handleImageLoad(node);
                                    });
                                }
                            } 
                            // Check for images within added HTML
                            else if (node.querySelectorAll) {
                                const images = node.querySelectorAll('img');
                                
                                for (let j = 0; j < images.length; j++) {
                                    const img = images[j];
                                    if (img.complete) {
                                        handleImageLoad(img);
                                    } else {
                                        img.addEventListener('load', function() {
                                            handleImageLoad(img);
                                        });
                                    }
                                }
                            }
                        }
                    }
                });
            });
            
            // Start observing the editor content
            observer.observe(editableElement, {
                childList: true,  // Watch for child node changes
                subtree: true,    // Watch the entire subtree for changes
                characterData: false,
                attributes: false
            });
            
            // Store the observer on the editor instance to clean it up later if needed
            ckeditor.mutationObserver = observer;
            
            // Clean up the observer when the editor is destroyed
            ckeditor.on('destroy', function() {
                if (ckeditor.mutationObserver) {
                    ckeditor.mutationObserver.disconnect();
                    ckeditor.mutationObserver = null;
                }
            });
        });
        
        // Handle image paste events
        ckeditor.on('paste', async function(evt) {
            try {
                // Get clipboard data for analysis
                const clipboardData = evt.data.dataTransfer || evt.originalEvent?.clipboardData;
                
                // Check if this is a text paste that should proceed normally
                const isTextPaste = checkIfTextPaste(evt, clipboardData);
                if (isTextPaste) {
                    return; // Allow the event to proceed normally
                }
                
                // If we get here, it's not a text paste, so stop default behavior
                evt.stop();
                
                // Check for actual image content before assuming it's a file reference
                const hasActualImageContent = await checkForActualImageContent(clipboardData, evt);
                
                // Check for empty clipboard with async clipboard API available,
                // but only if we haven't already detected actual image content
                if (!hasActualImageContent) {
                    const hasEmptyClipboard = clipboardData && 
                        (!clipboardData.types || clipboardData.types.length === 0) &&
                        (!clipboardData.files || clipboardData.files.length === 0);
                    
                    if (hasEmptyClipboard && navigator.clipboard) {
                        // Show helpful message about image handling
                        Vue.$toast.info(
                            "To add an image: 1) Drag and drop an image file directly into the editor, or 2) Open an image in an application and copy its visual content.",
                            { 
                                duration: 10000,
                                position: "top-center",
                            }
                        );
                        
                        evt.cancel();
                        return;
                    }
                }

                // Try using Async Clipboard API first
                if (navigator.clipboard && navigator.clipboard.read) {
                    try {
                        const clipboardItems = await navigator.clipboard.read();
                        
                        if (clipboardItems && clipboardItems.length > 0) {
                            const firstItem = clipboardItems[0];
                            const types = await firstItem.types;
                            
                            // Only show warning if there are no image types and no content
                            if (types.length === 0 && !evt.data.dataValue && !hasActualImageContent) {
                                // One more check for text content
                                if (!checkIfTextPaste(evt, clipboardData)) {
                                    Vue.$toast.info(
                                        "To add an image: 1) Drag and drop an image file directly into the editor, or 2) Open an image in an application and copy its visual content.",
                                        { 
                                            duration: 10000,
                                            position: "top-center",
                                        }
                                    );
                                    evt.cancel();
                                    return;
                                }
                            }
                            
                            for (const type of types) {
                                if (type.startsWith('image/')) {
                                    const blob = await firstItem.getType(type);
                                    const file = new File([blob], 'pasted-image.png', { type: type });
                                    await handleImageFile(file, evt, ckeditor, 'paste');
                                    return;
                                }
                            }
                        }
                    } catch (err) {
                        // Fall back to event data
                    }
                }

                // Continue with standard handling for image files
                if (!clipboardData) {
                    evt.cancel();
                    return;
                }

                // Handle direct files
                if (clipboardData.files && clipboardData.files.length > 0) {
                    const file = clipboardData.files[0];
                    if (file && file.type.startsWith('image/')) {
                        await handleImageFile(file, evt, ckeditor, 'paste');
                        return;
                    }
                }

                // Handle clipboard items
                if (clipboardData.items) {
                    for (const item of clipboardData.items) {
                        if (item.type.startsWith('image/')) {
                            const file = item.getAsFile();
                            if (file) {
                                await handleImageFile(file, evt, ckeditor, 'paste');
                                return;
                            }
                        }
                    }
                }

                // If we get here, we didn't find anything to handle
                evt.cancel(); // Cancel our handling so default can take over
            } catch (error) {
                evt.cancel();
            }
        });

        // Helper function to check if the paste event contains actual image content
        async function checkForActualImageContent(clipboardData, evt) {
            // First check for image data in clipboardData items
            if (clipboardData && clipboardData.items) {
                for (const item of clipboardData.items) {
                    // Check for image MIME types
                    if (item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (file && file.size > 0) {
                            return true;
                        }
                    }
                }
            }
            
            // Check for image files in clipboardData files
            if (clipboardData && clipboardData.files && clipboardData.files.length > 0) {
                for (let i = 0; i < clipboardData.files.length; i++) {
                    const file = clipboardData.files[i];
                    if (file.type.startsWith('image/') && file.size > 0) {
                        return true;
                    }
                }
            }
            
            // Check for HTML containing image data (like base64 encoded images)
            if (clipboardData && clipboardData.getData) {
                try {
                    const html = clipboardData.getData('text/html');
                    if (html && html.includes('<img') && (html.includes('src="data:image') || html.includes("src='data:image"))) {
                        return true;
                    }
                } catch (e) {
                    // Error checking clipboard HTML
                }
            }
            
            // Check event data for image content
            if (evt.data && evt.data.dataValue && 
                ((typeof evt.data.dataValue === 'string' && 
                  (evt.data.dataValue.includes('<img') || evt.data.dataValue.includes('data:image'))) ||
                 evt.data.dataValue.includes('image/'))) {
                return true;
            }
            
            // Try async clipboard API if available
            if (navigator.clipboard && navigator.clipboard.read) {
                try {
                    const clipboardItems = await navigator.clipboard.read();
                    if (clipboardItems && clipboardItems.length > 0) {
                        const firstItem = clipboardItems[0];
                        const types = await firstItem.types;
                        
                        // Check for image types
                        for (const type of types) {
                            if (type.startsWith('image/')) {
                                try {
                                    const blob = await firstItem.getType(type);
                                    if (blob && blob.size > 0) {
                                        return true;
                                    }
                                } catch (e) {
                                    // Error getting blob from async clipboard
                                }
                            }
                        }
                    }
                } catch (e) {
                    // Error checking async clipboard for images
                }
            }
            
            return false;
        }

        // Helper function to check if the paste event contains text
        function checkIfTextPaste(evt, clipboardData) {
            // Check for text in dataValue (CKEditor's processed content)
            if (evt.data && evt.data.dataValue && evt.data.dataValue.trim().length > 0) {
                return true;
            }
            
            // Check clipboard data directly
            if (clipboardData && clipboardData.getData) {
                try {
                    // Check for plain text
                    const text = clipboardData.getData('text/plain');
                    if (text && text.trim().length > 0) {
                        return true;
                    }
                    
                    // Check for HTML text
                    const html = clipboardData.getData('text/html');
                    if (html && html.trim().length > 0) {
                        return true;
                    }
                } catch (e) {
                    // Error getting clipboard text
                }
            }
            
            // Check for other text indicators
            if (evt.data && (
                (evt.data.text && evt.data.text.trim().length > 0) || 
                (evt.data.html && evt.data.html.trim().length > 0)
            )) {
                return true;
            }
            
            return false;
        }

        // Handle drag and drop
        ckeditor.on('drop', async function(evt) {
            try {
                // Get the native DataTransfer object from CKEditor's wrapper
                const nativeDataTransfer = evt.data.dataTransfer?.$;
                
                // Check if we have valid dataTransfer with files
                if (nativeDataTransfer?.files?.length > 0) {
                    const file = nativeDataTransfer.files[0];
                    
                    // Ensure file is defined and is an image
                    if (file?.type?.startsWith('image/')) {
                        evt.stop();
                        
                        // Create a new File object to ensure we're not using cached data
                        const freshFile = new File([file], file.name, {
                            type: file.type,
                            lastModified: Date.now()
                        });
                        
                        await handleImageFile(freshFile, evt, ckeditor, 'drop');
                    } else if (file) {
                        // File exists but is not an image
                        evt.stop();
                        evt.preventDefault();
                        evt.cancel();
                        
                        Vue.$toast.info(
                            "Only images can be added to this field. Please drag and drop an image file (JPG, PNG, GIF, etc).", 
                            { 
                                duration: 5000,
                                position: "top-center",
                            }
                        );
                        return;
                    }
                }
            } catch (error) {
                // Drop handling error
            }
        });

        // Helper function to handle image files
        async function handleImageFile(file, evt, editor, source = 'unknown') {
            // Check if file is an image with valid format
            if (!file.type.match(/image\/(jpeg|jpg|png|gif|bmp|webp|svg\+xml)/)) {
                Vue.$toast.error("Invalid image format.");
                return;
            }
            
            try {
                let processedFile = file;
                let originalFile = file; // Keep track of the very original file
                const isDroppedWebp = source === 'drop' && file.type === 'image/webp';

                // --- Step 1: Handle WEBP Conversion (if applicable) ---
                if (isDroppedWebp) {
                    try {
                        Vue.$toast.info("Converting image to compatible format...");
                        processedFile = await ImageUtils.convertWebpToPng(originalFile);
                        // Check if conversion actually happened (might return original on error)
                        if (processedFile.type === 'image/png') {
                            Vue.$toast.success("Image converted successfully.");
                        } else {
                            // Conversion failed internally, error toast was likely shown in converter
                             return; // Stop processing if conversion failed
                        }
                    } catch (conversionError) {
                         // Error already logged and toasted in convertWebpToPng
                        return; // Stop processing if conversion fails
                    }
                }

                // --- Step 2: Handle Compression/Resizing (for original or converted file) ---
                const currentFileToCheck = processedFile; // Use the potentially converted file for checks
                const fileSizeMB = currentFileToCheck.size / (1024 * 1024);
                let needsResizeCheck = false;
                // Avoid redundant dimension check if conversion failed or wasn't needed
                if(currentFileToCheck.size > 0){
                    needsResizeCheck = await ImageUtils.needsResizing(currentFileToCheck);
                }


                if (fileSizeMB > 5 || needsResizeCheck) {
                     // Determine if we should force resize (always true if needsResizeCheck is true)
                     // Note: We don't 'force' resize just because size > 5MB, compressImage handles that.
                     // We force if dimensions are too large.
                     const forceResize = needsResizeCheck;

                    try {
                        const message = fileSizeMB > 5 ? "Compressing large image..." : "Resizing image dimensions...";
                        Vue.$toast.info(message);

                        const originalSize = currentFileToCheck.size;
                        // Call the original compressImage with the current file (might be original or the PNG)
                        processedFile = await ImageUtils.compressImage(currentFileToCheck, 5, forceResize);

                        // Check size after processing
                        if (processedFile.size / (1024 * 1024) > 5) {
                            Vue.$toast.error("Image is too large even after processing (max 5MB).");
                            return;
                        }

                        // Show success message only if action was taken and size changed
                        if (processedFile !== currentFileToCheck && Math.abs(originalSize - processedFile.size) > (originalSize * 0.01)) { // Use 1% threshold for notification
                            if (fileSizeMB > 5) {
                                Vue.$toast.success("Image compressed successfully.");
                            } else if (needsResizeCheck) {
                                Vue.$toast.success("Image resized successfully.");
                            }
                        }

                    } catch (processingError) {
                        console.error("Image compression/resize error:", processingError);
                        const failureMessage = fileSizeMB > 5 ? "Failed to compress image. Size must be under 5MB." : "Failed to resize image.";
                        Vue.$toast.error(failureMessage);
                        // Attempt to proceed with the file we had before this step failed?
                        // Or just stop? Stopping seems safer.
                        return; // Stop if compression/resize fails
                    }
                }

                // --- Step 3: Uploading and Insertion Logic ---
                // At this point, processedFile is either the original, the converted PNG,
                // or the compressed/resized version of either.
                const response = await ApiService.uploadImage(processedFile);
                const imageUrl = response.data.url;
                
                // Create a unique cache key that includes the source and a timestamp
                const cacheKey = `${imageUrl}?source=${source}&t=${Date.now()}`;
                
                // Create a blob URL for the image and store it in the cache
                const blobUrl = URL.createObjectURL(processedFile);
                normalizedImageCache.set(cacheKey, blobUrl);

                // Get dimensions of the processed file to set initial attributes/style
                let initialWidth, initialHeight;
                try {
                    const dimensions = await ImageUtils.getImageDimensions(processedFile);
                    initialWidth = dimensions.width;
                    initialHeight = dimensions.height;
                } catch (dimError) {
                    console.error("Could not get processed image dimensions, inserting without initial size.", dimError);
                    // Insert without dimensions if we can't get them
                    const imageHtml = `<img src="${blobUrl}" alt="Uploaded image" data-source="${source}">`;
                    editor.insertHtml(imageHtml);
                    cleanupOldCacheEntries();
                    return; // Exit early
                }
                
                // Insert the blob URL with initial dimensions in attributes and style
                const imageHtml = `<img src="${blobUrl}" alt="Uploaded image" data-source="${source}" width="${initialWidth}" height="${initialHeight}" style="width: ${initialWidth}px; height: ${initialHeight}px;">`;
                editor.insertHtml(imageHtml);
    

                // Clean up old cache entries periodically
                cleanupOldCacheEntries();
            } catch (error) {
                Vue.$toast.error("Failed to upload image. Please try again.");
            }
        }
        
        // Helper function to clean up old cache entries
        function cleanupOldCacheEntries() {
            const now = Date.now();
            const maxAge = 30 * 60 * 1000; // 30 minutes
            
            for (const [key, blobUrl] of normalizedImageCache.entries()) {
                // Extract timestamp from cache key
                const match = key.match(/t=(\d+)/);
                if (match) {
                    const timestamp = parseInt(match[1]);
                    if (now - timestamp > maxAge) {
                        URL.revokeObjectURL(blobUrl);
                        normalizedImageCache.delete(key);
                    }
                }
            }
        }
    }
}
